import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, Download, ChevronRight, Car, Clock, Filter, AlertTriangle, ClipboardList, Truck, Trash2, Send, CalendarDays, List, User, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const STATUS_TABS = [
  { key: 'all', label: 'Toutes', color: 'bg-slate-900 text-white' },
  { key: 'atelier_ready', label: 'Pret atelier', color: 'bg-emerald-600 text-white' },
  { key: 'unbilled', label: 'Non facturé', color: 'bg-red-600 text-white' },
  { key: 'open', label: 'A venir', color: 'bg-blue-600 text-white' },
  { key: 'signed', label: 'Signees', color: 'bg-emerald-600 text-white' },
  { key: 'in_progress', label: 'En cours', color: 'bg-amber-500 text-white' },
  { key: 'completed', label: 'Terminées', color: 'bg-slate-500 text-white' },
];

const STATUS_CONFIG = {
  open: { label: 'A venir', color: 'bg-blue-100 text-blue-700' },
  signed: { label: 'Signee', color: 'bg-emerald-100 text-emerald-700' },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Terminée', color: 'bg-slate-100 text-slate-700' },
};

import AtelierDashboardAdmin from './AtelierDashboardAdmin';
import { ConfirmBilledButton } from '@/components/workorder/ConfirmBilledButton';

const isUnbilled = (w) => w.atelier_ready && !w.billed_confirmed && w.billing_type !== 'interne';

const WorkOrdersListPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const role = auth?.admin?.role || 'admin';

  // Atelier role gets a dedicated dashboard
  if (role === 'atelier') {
    return <AtelierDashboardAdmin auth={auth} onLogout={onLogout} />;
  }

  return <WorkOrdersListAdmin auth={auth} onLogout={onLogout} />;
};

const WorkOrdersListAdmin = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [overdueAlerts, setOverdueAlerts] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [viewMode, setViewMode] = useState('planning');
  const [showAllOverdue, setShowAllOverdue] = useState(false);
  const navigate = useNavigate();
  const isAdmin = auth?.admin?.role === 'admin' || auth?.admin?.is_admin === true;

  // Real-time WebSocket for overdue reminders only (vehicle ready handled by AdminLayout)

  useEffect(() => {
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/notifications`;
    let ws;
    let reconnectTimer;
    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'overdue_reminder' && data.overdue_work_orders?.length > 0) {
            setOverdueAlerts(data.overdue_work_orders);
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              const now = ctx.currentTime;
              for (let i = 0; i < 3; i++) {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain); gain.connect(ctx.destination);
                osc.type = 'square'; osc.frequency.value = 1200;
                gain.gain.setValueAtTime(0.2, now + i * 0.25);
                gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.25 + 0.15);
                osc.start(now + i * 0.25); osc.stop(now + i * 0.25 + 0.15);
              }
            } catch { /* ignore */ }
            data.overdue_work_orders.forEach(ow => {
              const label = ow.display_label || ow.vehicle_info || ow.reference;
              const plate = ow.vehicle_plate ? ` (${ow.vehicle_plate})` : '';
              toast.warning(`Depassement: ${label}${plate}`, {
                description: `${ow.actual_hours}h / ${ow.allocated_hours}h (+${ow.overflow_hours}h)`,
                duration: 15000,
              });
            });
          }
          // Refresh list when client signs or responds to additional work
          if (data.type === 'work_order_signed' || data.type === 'additional_work_response') {
            toast.success(data.message || 'Mise à jour client', { duration: 8000 });
            loadWorkOrders();
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };
    connect();
    return () => { clearTimeout(reconnectTimer); ws?.close(); };
  }, []);

  const [apiError, setApiError] = useState(null);

  const loadWorkOrders = useCallback(async () => {
    try {
      setApiError(null);
      const res = await axios.get(`${API}/work-orders`);
      if (Array.isArray(res.data)) {
        setWorkOrders(res.data);
      } else {
        setApiError(`Reponse inattendue: ${typeof res.data}`);
        setWorkOrders([]);
      }
    } catch (err) {
      console.error('Work orders load error:', err);
      setApiError(`Erreur: ${err?.response?.status || err?.message || 'inconnu'} - ${err?.response?.data?.detail || ''}`);
      // Fallback: try the simple endpoint
      try {
        const fallback = await axios.get(`${API}/work-orders-simple`);
        if (fallback.data?.work_orders) {
          setWorkOrders(fallback.data.work_orders);
          setApiError(`Charge via fallback (${fallback.data.count} fiches)`);
        }
      } catch { /* ignore fallback error */ }
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadWorkOrders(); }, [loadWorkOrders]);

  // Auto-refresh every 30s to pick up atelier_ready changes
  useEffect(() => {
    const interval = setInterval(loadWorkOrders, 30000);
    return () => clearInterval(interval);
  }, [loadWorkOrders]);

  const counts = {
    all: workOrders.length,
    atelier_ready: workOrders.filter(w => w.atelier_ready && w.status !== 'completed').length,
    unbilled: workOrders.filter(isUnbilled).length,
    open: workOrders.filter(w => w.status === 'open').length,
    signed: workOrders.filter(w => w.status === 'signed').length,
    in_progress: workOrders.filter(w => w.status === 'in_progress').length,
    completed: workOrders.filter(w => w.status === 'completed').length,
  };

  const filtered = workOrders.filter(wo => {
    if (statusFilter === 'unbilled') {
      if (!isUnbilled(wo)) return false;
    } else if (statusFilter === 'atelier_ready') {
      if (!wo.atelier_ready || wo.status === 'completed') return false;
    } else if (statusFilter !== 'all' && wo.status !== statusFilter) return false;
    if (search) {
      const term = search.toLowerCase();
      return (wo.client_name || '').toLowerCase().includes(term) ||
             (wo.vehicle_info || '').toLowerCase().includes(term) ||
             wo.id.toLowerCase().includes(term);
    }
    return true;
  });

  // Planning groups: sort by date and group by period
  const planningGroups = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const endOfWeek = new Date(today); endOfWeek.setDate(today.getDate() + (7 - today.getDay()));

    const getDate = (wo) => {
      if (wo.appointment_date) return new Date(wo.appointment_date);
      if (wo.created_at) return new Date(wo.created_at);
      return null;
    };

    const statusPriority = { in_progress: 0, en_cours: 0, signed: 1, open: 2, completed: 3 };

    // Sort: active first, then by date (earliest first)
    const sorted = [...filtered].sort((a, b) => {
      const pa = statusPriority[a.status] ?? 2;
      const pb = statusPriority[b.status] ?? 2;
      if (pa !== pb) return pa - pb;
      const da = getDate(a), db = getDate(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da - db;
    });

    const groups = [
      { key: 'overdue', label: 'En retard', items: [], color: 'border-red-500 bg-red-50', textColor: 'text-red-700', dot: 'bg-red-500' },
      { key: 'today', label: "Aujourd'hui", items: [], color: 'border-blue-500 bg-blue-50', textColor: 'text-blue-700', dot: 'bg-blue-500' },
      { key: 'tomorrow', label: 'Demain', items: [], color: 'border-amber-500 bg-amber-50', textColor: 'text-amber-700', dot: 'bg-amber-500' },
      { key: 'week', label: 'Cette semaine', items: [], color: 'border-slate-400 bg-slate-50', textColor: 'text-slate-700', dot: 'bg-slate-400' },
      { key: 'later', label: 'Plus tard', items: [], color: 'border-slate-300 bg-white', textColor: 'text-slate-500', dot: 'bg-slate-300' },
      { key: 'nodate', label: 'Sans date', items: [], color: 'border-slate-200 bg-white', textColor: 'text-slate-400', dot: 'bg-slate-200' },
    ];

    sorted.forEach(wo => {
      if (wo.status === 'completed') { groups[4].items.push(wo); return; }
      const d = getDate(wo);
      if (!d) { groups[5].items.push(wo); return; }
      const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (dDay < today) groups[0].items.push(wo);
      else if (dDay.getTime() === today.getTime()) groups[1].items.push(wo);
      else if (dDay.getTime() === tomorrow.getTime()) groups[2].items.push(wo);
      else if (dDay <= endOfWeek) groups[3].items.push(wo);
      else groups[4].items.push(wo);
    });

    return groups.filter(g => g.items.length > 0);
  }, [filtered]);

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-slate-900" data-testid="workorders-title">{t('work_orders.title')}</h1>
          <p className="text-base text-slate-600 mt-1">{workOrders.length} fiche(s)</p>
        </div>

        {apiError && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-sm text-amber-800" data-testid="api-error-banner">
            {apiError}
          </div>
        )}

        {overdueAlerts.length > 0 && (
          <Card className="p-4 rounded-xl bg-red-50 border-2 border-red-300" data-testid="overdue-alerts-banner-admin">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-red-800 text-sm">Depassement de temps detecte</h3>
                <div className="mt-1 space-y-1">
                  {overdueAlerts.map(ow => (
                    <button key={ow.work_order_id} onClick={() => navigate(`/admin/work-orders/${ow.work_order_id}`)}
                      className="w-full text-left flex items-center justify-between px-2 py-1.5 rounded bg-red-100 hover:bg-red-200 transition-colors" data-testid={`overdue-admin-${ow.work_order_id}`}>
                      <span className="text-xs font-medium text-red-900">{ow.display_label || ow.vehicle_info || ow.reference} {ow.vehicle_plate && `(${ow.vehicle_plate})`}</span>
                      <span className="text-xs font-bold text-red-700">{ow.actual_hours}h / {ow.allocated_hours}h (+{ow.overflow_hours}h)</span>
                    </button>
                  ))}
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => setOverdueAlerts([])} data-testid="dismiss-overdue-admin">
                Fermer
              </Button>
            </div>
          </Card>
        )}

        {/* Status Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="workorder-status-tabs">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                statusFilter === tab.key
                  ? tab.color + ' shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-tab-${tab.key}`}
            >
              {tab.label}
              <Badge
                className={`text-[10px] px-1.5 py-0 rounded-full ${
                  statusFilter === tab.key ? 'bg-white/20 text-inherit' : 'bg-slate-200 text-slate-600'
                }`}
              >
                {counts[tab.key]}
              </Badge>
            </button>
          ))}
        </div>

        {/* Stats Chart */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="workorder-stats-chart">
          {[
            { key: 'atelier_ready', label: 'Pret atelier', count: counts.atelier_ready, color: 'bg-emerald-500', textColor: 'text-emerald-600', bgLight: 'bg-emerald-50' },
            { key: 'open', label: 'A venir', count: counts.open, color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-50' },
            { key: 'signed', label: 'Signees', count: counts.signed, color: 'bg-emerald-500', textColor: 'text-emerald-600', bgLight: 'bg-emerald-50' },
            { key: 'in_progress', label: 'En cours', count: counts.in_progress, color: 'bg-amber-500', textColor: 'text-amber-600', bgLight: 'bg-amber-50' },
            { key: 'completed', label: 'Terminées', count: counts.completed, color: 'bg-slate-500', textColor: 'text-slate-600', bgLight: 'bg-slate-100' },
          ].map(s => {
            const pct = workOrders.length > 0 ? (s.count / workOrders.length) * 100 : 0;
            return (
              <Card
                key={s.key}
                className={`p-3 rounded-xl cursor-pointer transition-all border-2 ${statusFilter === s.key ? 'border-slate-900 shadow-md' : 'border-transparent hover:border-slate-200'}`}
                onClick={() => setStatusFilter(statusFilter === s.key ? 'all' : s.key)}
                data-testid={`stat-card-${s.key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-2xl font-bold ${s.textColor}`}>{s.count}</span>
                  <span className="text-xs text-slate-400">{pct.toFixed(0)}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-1.5">
                  <div className={`h-full rounded-full ${s.color} transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-xs font-medium text-slate-600">{s.label}</p>
              </Card>
            );
          })}
        </div>

        {/* Search + View Toggle */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder={t('work_orders.search_wo')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="workorder-search"
            />
          </div>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode('planning')}
              className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${viewMode === 'planning' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              data-testid="view-planning"
            >
              <CalendarDays className="w-4 h-4" /> Planning
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              data-testid="view-list"
            >
              <List className="w-4 h-4" /> Liste
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <p className="text-center text-slate-500 py-8">{t('common.loading')}</p>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center rounded-xl">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{search || statusFilter !== 'all' ? 'Aucun resultat' : 'Aucune fiche de travail'}</p>
          </Card>
        ) : viewMode === 'planning' ? (
          /* ===== PLANNING VIEW ===== */
          <div className="space-y-6" data-testid="planning-view">
            {planningGroups.map(group => {
              const isOverdue = group.key === 'overdue';
              const MAX_VISIBLE = 3;
              const visibleItems = isOverdue && !showAllOverdue ? group.items.slice(0, MAX_VISIBLE) : group.items;
              const hiddenCount = isOverdue ? group.items.length - MAX_VISIBLE : 0;
              return (
              <div key={group.key}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-3 h-3 rounded-full ${group.dot}`} />
                  <h2 className={`text-lg font-bold ${group.textColor}`}>{group.label}</h2>
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{group.items.length}</span>
                </div>
                <div className="grid gap-2 pl-6 border-l-2 border-slate-100">
                  {visibleItems.map(wo => (
                    <WOCard key={wo.id} wo={wo} navigate={navigate} isAdmin={isAdmin}
                      confirmDeleteId={confirmDeleteId} setConfirmDeleteId={setConfirmDeleteId}
                      loadWorkOrders={loadWorkOrders} showDate />
                  ))}
                  {isOverdue && hiddenCount > 0 && !showAllOverdue && (
                    <button
                      onClick={() => setShowAllOverdue(true)}
                      className="text-sm text-red-600 hover:text-red-800 font-medium py-2 px-3 rounded-lg hover:bg-red-50 transition-colors text-left"
                      data-testid="show-all-overdue"
                    >
                      + {hiddenCount} autre{hiddenCount > 1 ? 's' : ''} en retard...
                    </button>
                  )}
                  {isOverdue && showAllOverdue && group.items.length > MAX_VISIBLE && (
                    <button
                      onClick={() => setShowAllOverdue(false)}
                      className="text-sm text-slate-500 hover:text-slate-700 font-medium py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                      data-testid="collapse-overdue"
                    >
                      Reduire
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          /* ===== LIST VIEW ===== */
          <div className="grid gap-3" data-testid="list-view">
            {filtered.map((wo) => (
              <WOCard key={wo.id} wo={wo} navigate={navigate} isAdmin={isAdmin}
                confirmDeleteId={confirmDeleteId} setConfirmDeleteId={setConfirmDeleteId}
                loadWorkOrders={loadWorkOrders} showDate={false} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

/* ===== Reusable Work Order Card ===== */
const WOCard = ({ wo, navigate, isAdmin, confirmDeleteId, setConfirmDeleteId, loadWorkOrders, showDate }) => {
  const { t } = useTranslation();
  const st = STATUS_CONFIG[wo.status] || STATUS_CONFIG.open;
  const isLivraison = wo.source === 'preparation_livraison';
  const isParc = wo.source === 'preparation_vo';
  const cardBorder = isLivraison ? 'border-orange-300 bg-orange-50/40' : isParc ? 'border-sky-300 bg-sky-50/40' : 'border-slate-200';
  const iconBg = isLivraison ? 'bg-orange-500' : isParc ? 'bg-sky-600' : 'bg-slate-900';
  const TypeIcon = isLivraison ? Truck : isParc ? ClipboardList : FileText;

  const dateStr = wo.appointment_date
    ? `${new Date(wo.appointment_date).toLocaleDateString('fr-CH', { weekday: 'short', day: 'numeric', month: 'short' })}${wo.appointment_time ? ' ' + wo.appointment_time : ''}`
    : '';

  return (
    <Card
      className={`p-3 rounded-xl border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${cardBorder}`}
      onClick={() => navigate(`/admin/work-orders/${wo.id}`)}
      data-testid={`workorder-card-${wo.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {showDate && dateStr ? (
            <div className="w-16 h-14 rounded-lg bg-slate-900 text-white flex flex-col items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-medium leading-none opacity-80">
                {new Date(wo.appointment_date).toLocaleDateString('fr-CH', { weekday: 'short' })}
              </span>
              <span className="text-lg font-bold leading-tight">
                {new Date(wo.appointment_date).getDate()}
              </span>
              <span className="text-[10px] leading-none opacity-80">
                {wo.appointment_time || new Date(wo.appointment_date).toLocaleDateString('fr-CH', { month: 'short' })}
              </span>
            </div>
          ) : (
            <div className={`w-10 h-10 rounded-full ${iconBg} text-white flex items-center justify-center flex-shrink-0`}>
              <TypeIcon className="w-5 h-5" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold text-slate-900 truncate">{wo.client_name && wo.client_name.trim() ? wo.client_name : (wo.reference ? `Client #${wo.reference}` : 'Client inconnu')}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${st.color}`}>{st.label}</span>
              {wo.billing_type === 'devis' && (
                <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700 border border-amber-300" data-testid={`badge-devis-${wo.id}`}>DEVIS</span>
              )}
              {wo.billing_type === 'reparation' && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700" data-testid={`badge-reparation-${wo.id}`}>Réparation</span>
              )}
              {isLivraison && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">Livraison</span>}
              {isParc && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-sky-100 text-sky-700">Parc Occasion</span>}
              {wo.atelier_ready && wo.status !== 'completed' && (
                <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-emerald-500 text-white animate-pulse">PRET ATELIER</span>
              )}
              {isUnbilled(wo) && (
                <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-red-100 text-red-700 border border-red-300" data-testid={`badge-unbilled-${wo.id}`}>NON FACTURÉ</span>
              )}
              {wo.parts_orders && wo.parts_orders.length > 0 && (() => {
                const notOrdered = wo.parts_orders.filter(o => o.status === 'pas_commande').length;
                const pending = wo.parts_orders.filter(o => o.status === 'en_cours').length;
                if (notOrdered > 0) return <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-red-100 text-red-700 border border-red-200" data-testid={`parts-badge-${wo.id}`}>{notOrdered} piece(s) a commander</span>;
                if (pending > 0) return <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700 border border-amber-200" data-testid={`parts-badge-${wo.id}`}>{pending} en attente</span>;
                return null;
              })()}
              {wo.satisfaction_rating && (
                <span className="flex items-center gap-0.5" data-testid={`satisfaction-stars-${wo.id}`}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Star key={i} className={`w-3 h-3 ${i <= wo.satisfaction_rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                  ))}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
              <span className="flex items-center gap-1 truncate"><Car className="w-3 h-3 flex-shrink-0" /> {wo.vehicle_info || '-'}</span>
              {(wo.current_mechanic || wo.assigned_employee_name) && (
                <span className="flex items-center gap-1 text-indigo-600 font-medium"><User className="w-3 h-3" /> {wo.current_mechanic || wo.assigned_employee_name}</span>
              )}
              {wo.status === 'in_progress' && wo.allocated_hours > 0 && (
                <span className={`flex items-center gap-1 font-bold ${wo.actual_hours > wo.allocated_hours ? 'text-red-600' : 'text-emerald-600'}`}>
                  <Clock className="w-3 h-3" /> {wo.actual_hours || 0}h / {wo.allocated_hours}h
                </span>
              )}
              {wo.status === 'in_progress' && !wo.allocated_hours && wo.actual_hours > 0 && (
                <span className="flex items-center gap-1 font-medium text-amber-600">
                  <Clock className="w-3 h-3" /> {wo.actual_hours}h
                </span>
              )}
              {!showDate && !wo.current_mechanic && !wo.assigned_employee_name && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {wo.created_at?.substring(0, 10)}</span>}
              {wo.invoice_number && <span className="font-mono font-bold text-indigo-600">{wo.invoice_number}</span>}
            </div>
            {wo.pickup_date && (
              <div className="flex items-center gap-1.5 mt-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-lg w-fit" data-testid={`pickup-info-${wo.id}`}>
                <CalendarDays className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs font-bold text-emerald-700">
                  Reprise: {new Date(wo.pickup_date).toLocaleDateString('fr-CH', { weekday: 'short', day: 'numeric', month: 'short' })}{wo.pickup_time ? ` a ${wo.pickup_time}` : ''}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isUnbilled(wo) && (
            <div className="flex items-center gap-1 mr-1" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="outline" onClick={() => window.open(`/admin/work-orders/${wo.id}/detail`, '_blank')} data-testid={`print-unbilled-${wo.id}`}>Imprimer</Button>
              <ConfirmBilledButton wo={wo} size="sm" onDone={loadWorkOrders} />
            </div>
          )}
          <Button variant="ghost" size="sm"
            onClick={async (e) => { e.stopPropagation();
              try { const { downloadPdf } = await import('@/utils/api'); await downloadPdf(`${API}/work-orders/${wo.id}/pdf`, `fiche_travail_${wo.id.substring(0, 8)}.pdf`); }
              catch (err) { console.error('PDF download error:', err); }
            }}
            data-testid={`download-pdf-${wo.id}`}
          >
            <Download className="w-4 h-4" />
          </Button>
          {isAdmin ? (
            confirmDeleteId === wo.id ? (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-100 text-xs font-bold"
                  onClick={async (e) => { e.stopPropagation();
                    try { await axios.delete(`${API}/work-orders/${wo.id}`); toast.success('Fiche supprimee'); setConfirmDeleteId(null); loadWorkOrders(); }
                    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                  }}
                  data-testid={`confirm-delete-wo-${wo.id}`}>{t('common.confirm')}</Button>
                <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 text-xs"
                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                  data-testid={`cancel-delete-wo-${wo.id}`}>{t('common.cancel')}</Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(wo.id); }}
                data-testid={`delete-wo-${wo.id}`}><Trash2 className="w-4 h-4" /></Button>
            )
          ) : (
            <Button variant="ghost" size="sm" className="text-amber-500 hover:text-amber-700 hover:bg-amber-50"
              onClick={async (e) => { e.stopPropagation();
                try { await axios.post(`${API}/deletion-requests`, { type: 'delete_work_order', target_id: wo.id, target_label: `OT ${wo.client_name} - ${wo.description?.substring(0, 40) || ''}` });
                  toast.success("Demande de suppression envoyee"); }
                catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
              }}
              data-testid={`request-delete-wo-${wo.id}`} title="Demander la suppression">
              <Send className="w-4 h-4" />
            </Button>
          )}
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </div>
      </div>
    </Card>
  );
};

export default WorkOrdersListPage;
